import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneTilt, CheckCircle, Buildings, User, Envelope, Phone, Calendar, Users } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProQuoteForm() {
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    email: "",
    phone: "",
    event_date: "",
    guest_count: "",
    formula: "pro-print",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company.trim() || !form.email.trim() || !form.formula) {
      toast.error("Merci de renseigner l'entreprise, l'email et la formule.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        company: form.company.trim(),
        contact_name: form.contact_name.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        event_date: form.event_date || null,
        guest_count: form.guest_count ? parseInt(form.guest_count, 10) : null,
        formula: form.formula,
        message: form.message.trim() || null,
      };
      await axios.post(`${API}/pro-quote`, payload);
      setSent(true);
      toast.success("Votre demande a bien été envoyée !");
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Merci de réessayer ou de nous appeler.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div
        className="rounded-2xl bg-[#0A0A0A] border-2 border-[#C9A962]/60 p-10 md:p-14 text-center max-w-2xl mx-auto"
        data-testid="pro-quote-success"
      >
        <div className="w-16 h-16 rounded-full bg-[#C9A962]/15 border border-[#C9A962]/50 mx-auto flex items-center justify-center mb-5">
          <CheckCircle size={36} weight="fill" className="text-[#C9A962]" />
        </div>
        <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3">
          Demande envoyée !
        </h3>
        <p className="text-[#B8A88A] mb-2">
          Nous vous envoyons votre <strong className="text-white">devis personnalisé sous 24h</strong>.
        </p>
        <p className="text-[#8A7A5A] text-sm">
          Un e-mail de confirmation vient de partir vers <strong>{form.email}</strong>.
        </p>
      </div>
    );
  }

  const inputBase =
    "w-full bg-[#000000] border border-[#C9A962]/30 rounded-xl px-4 py-3 text-white placeholder:text-[#5A5040] focus:border-[#C9A962] focus:outline-none focus:ring-2 focus:ring-[#C9A962]/30 transition-all";

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="rounded-2xl bg-[#0A0A0A] border border-[#C9A962]/25 p-6 md:p-10 max-w-3xl mx-auto"
      data-testid="pro-quote-form"
    >
      <div className="text-center mb-8">
        <p className="text-xs tracking-[0.3em] text-[#C9A962] mb-2">DEVIS EXPRESS</p>
        <h2 className="text-2xl md:text-3xl font-extrabold text-white">
          Recevez votre devis <span className="gradient-text">sous 24h</span>
        </h2>
        <p className="text-[#B8A88A] mt-2 text-sm">
          Remplissez ce formulaire — nous revenons vers vous rapidement.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <label className="block">
          <span className="flex items-center gap-1.5 text-[#C9A962] text-xs font-semibold mb-2 uppercase tracking-wider">
            <Buildings size={14} weight="fill" /> Entreprise *
          </span>
          <input
            type="text"
            required
            value={form.company}
            onChange={(e) => setField("company", e.target.value)}
            placeholder="Nom de votre société"
            className={inputBase}
            data-testid="pro-quote-company"
          />
        </label>

        <label className="block">
          <span className="flex items-center gap-1.5 text-[#C9A962] text-xs font-semibold mb-2 uppercase tracking-wider">
            <User size={14} weight="fill" /> Contact
          </span>
          <input
            type="text"
            value={form.contact_name}
            onChange={(e) => setField("contact_name", e.target.value)}
            placeholder="Nom et prénom"
            className={inputBase}
            data-testid="pro-quote-contact"
          />
        </label>

        <label className="block">
          <span className="flex items-center gap-1.5 text-[#C9A962] text-xs font-semibold mb-2 uppercase tracking-wider">
            <Envelope size={14} weight="fill" /> Email *
          </span>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            placeholder="contact@entreprise.fr"
            className={inputBase}
            data-testid="pro-quote-email"
          />
        </label>

        <label className="block">
          <span className="flex items-center gap-1.5 text-[#C9A962] text-xs font-semibold mb-2 uppercase tracking-wider">
            <Phone size={14} weight="fill" /> Téléphone
          </span>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
            placeholder="07 XX XX XX XX"
            className={inputBase}
            data-testid="pro-quote-phone"
          />
        </label>

        <label className="block">
          <span className="flex items-center gap-1.5 text-[#C9A962] text-xs font-semibold mb-2 uppercase tracking-wider">
            <Calendar size={14} weight="fill" /> Date envisagée
          </span>
          <input
            type="date"
            value={form.event_date}
            onChange={(e) => setField("event_date", e.target.value)}
            className={`${inputBase} [color-scheme:dark]`}
            data-testid="pro-quote-date"
          />
        </label>

        <label className="block">
          <span className="flex items-center gap-1.5 text-[#C9A962] text-xs font-semibold mb-2 uppercase tracking-wider">
            <Users size={14} weight="fill" /> Invités estimés
          </span>
          <input
            type="number"
            min="1"
            value={form.guest_count}
            onChange={(e) => setField("guest_count", e.target.value)}
            placeholder="Ex : 80"
            className={inputBase}
            data-testid="pro-quote-guests"
          />
        </label>
      </div>

      {/* Formula picker */}
      <div className="mb-4">
        <span className="block text-[#C9A962] text-xs font-semibold mb-2 uppercase tracking-wider">
          Formule souhaitée *
        </span>
        <div className="grid grid-cols-2 gap-3">
          {[
            { id: "pro-digital", label: "PRO DIGITAL", price: "149 € HT" },
            { id: "pro-print", label: "PRO PRINT", price: "249 € HT" },
          ].map((opt) => {
            const active = form.formula === opt.id;
            return (
              <button
                type="button"
                key={opt.id}
                onClick={() => setField("formula", opt.id)}
                data-testid={`pro-quote-formula-${opt.id}`}
                className={`rounded-xl px-4 py-3 text-left transition-all border-2 ${
                  active
                    ? "bg-[#C9A962]/15 border-[#C9A962] text-white"
                    : "bg-[#000] border-[#C9A962]/25 text-[#B8A88A] hover:border-[#C9A962]/60"
                }`}
              >
                <div className="text-sm font-bold">{opt.label}</div>
                <div className={`text-xs mt-0.5 ${active ? "text-[#F5E6B8]" : "text-[#8A7A5A]"}`}>{opt.price}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Message */}
      <label className="block mb-6">
        <span className="text-[#C9A962] text-xs font-semibold mb-2 uppercase tracking-wider block">
          Précisions (facultatif)
        </span>
        <textarea
          rows={3}
          value={form.message}
          onChange={(e) => setField("message", e.target.value)}
          placeholder="Type d'événement, lieu, contraintes particulières..."
          className={`${inputBase} resize-none`}
          data-testid="pro-quote-message"
        />
      </label>

      <button
        type="submit"
        disabled={loading}
        data-testid="pro-quote-submit"
        className="w-full py-4 rounded-full font-bold text-black bg-gradient-to-r from-[#D4BA7A] to-[#C9A962] hover:shadow-[0_0_30px_rgba(201,169,98,0.6)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            Envoi en cours…
          </>
        ) : (
          <>
            <PaperPlaneTilt size={18} weight="fill" />
            Recevoir mon devis
          </>
        )}
      </button>

      <p className="text-[11px] text-center text-[#5A5040] mt-4">
        Vos données sont utilisées uniquement pour traiter votre demande (
        <a href="/politique-confidentialite" className="text-[#8A7A5A] hover:text-[#C9A962]">
          politique de confidentialité
        </a>
        ).
      </p>
    </motion.form>
  );
}
